import { QueryClientProvider } from '@tanstack/react-query';
import { Route, Switch } from 'wouter';
import { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { queryClient } from '@/lib/queryClient';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import HomePage from '@/pages/HomePage';
import MapPage from '@/pages/MapPage';
import BusinessPage from '@/pages/BusinessPage';
import AddBusinessPage from '@/pages/AddBusinessPage';
import AboutPage from '@/pages/AboutPage';
import LoginPage from '@/pages/LoginPage';
import AdminPage from '@/pages/AdminPage';
import AdminScrapingPage from '@/pages/AdminScrapingPage';
import JobsPage from '@/pages/JobsPage';
import MerchPage from '@/pages/MerchPage';
import BeautyOfTheWeekPage from '@/pages/BeautyOfTheWeekPage';
import BlkGptPage from '@/pages/BlkGptPage';
import BlkMartPage from '@/pages/BlkMartPage';
import CrownCattlePage from '@/pages/CrownCattlePage';
import HBCUPage from '@/pages/HBCUPage';

import BMZPage from '@/pages/BMZPage';

// Components
import Navigation from '@/components/Navigation';
import AuthProvider from '@/components/AuthProvider';
import Footer from '@/components/Footer';
import WelcomePopup from '@/components/WelcomePopup';

// Hooks
import { useWelcomePopup } from '@/hooks/useWelcomePopup';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { showPopup, closePopup } = useWelcomePopup();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider user={user}>
          <div className="min-h-screen bg-background">
            <Navigation user={user} />
            <main>
            <Switch>
              <Route path="/" component={HomePage} />
              <Route path="/map" component={MapPage} />
              <Route path="/business/:id" component={BusinessPage} />
              <Route path="/add-business" component={AddBusinessPage} />
              <Route path="/about" component={() => <AboutPage />} />
              <Route path="/jobs" component={JobsPage} />
              <Route path="/merch" component={MerchPage} />
              <Route path="/bmz" component={BMZPage} />
              <Route path="/bmz/:rest*" component={BMZPage} />
              <Route path="/beauty" component={BeautyOfTheWeekPage} />
              <Route path="/blkgpt" component={BlkGptPage} />
              <Route path="/blkmart" component={BlkMartPage} />
              <Route path="/crowncattle" component={CrownCattlePage} />
              <Route path="/login" component={LoginPage} />
              <Route path="/hbcu" component={HBCUPage} />
              <Route path="/admin" component={AdminPage} />
              <Route path="/admin/scraping" component={AdminScrapingPage} />
              <Route>
                <HomePage />
              </Route>
            </Switch>
          </main>
          <Footer />
          
          {/* Welcome Popup */}
          <WelcomePopup isOpen={showPopup} onClose={closePopup} />
        </div>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;